/** @module Import a card list (e.g. a Cardmarket export): read, check every line, then book. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Check, FileUp, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type components, problemFrom } from "@/api/client";
import { CardFace } from "@/components/card-face";
import { CardPicker } from "@/components/card-picker";
import { LocationSelect } from "@/components/location-select";
import { SourceInput } from "@/components/suggested-inputs";
import { PageHeader } from "@/components/page-header";
import { Segmented } from "@/components/ui/controls";
import { Sheet, useToast } from "@/components/ui/overlays";
import {
  type Candidate,
  candidateOf,
  type Choice,
  choiceOf,
  type Choices,
  choose,
  commitLines,
  type ImportRow,
  initialChoices,
  isOpen,
  isReady,
  summary,
} from "@/features/collection/import-lines";
import { fetchCard, printedNumber, variantLabel } from "@/lib/cards";
import { type Condition, CONDITIONS, LANGUAGES } from "@/lib/collection";
import { money } from "@/lib/format";

type Preview = components["schemas"]["ImportPreviewOut"];
type Mapping = Record<string, string | null>;
type Tab = "open" | "ready" | "all";

const PAGE = 100;
const STATUS_BADGE: Record<ImportRow["status"], string> = {
  matched: "badge-gain",
  ambiguous: "badge-warn",
  unmatched: "badge-loss",
  invalid: "badge-neutral",
};

async function readList(
  file: File,
  mapping: Mapping | null,
  language: string,
  condition: Condition,
): Promise<Preview> {
  const form = new FormData();
  form.append("file", file, file.name);
  form.append("language", language);
  form.append("condition", condition);
  if (mapping) form.append("mapping", JSON.stringify(mapping));
  const { data, response } = await api.POST("/api/v1/collection/imports/preview", {
    body: form as never,
    bodySerializer: (body: unknown) => body as FormData,
  });
  if (!data) throw problemFrom(response);
  return data;
}

function CandidateCard({ candidate }: { candidate: Candidate }) {
  const { t } = useTranslation();
  const card = candidate.card;
  return (
    <span className="row min-w-0 gap-[10px]">
      <span className="thumb">
        <CardFace printId={card.print_id} name={card.name} lazy />
      </span>
      <span className="flex min-w-0 flex-col text-left">
        <b className="t-sm truncate">{card.name}</b>
        <span className="t-xs t-muted num truncate">
          {printedNumber(card)}
          {card.rarity ? ` · ${card.rarity}` : ""}
        </span>
      </span>
      <span className="sr-only">{t("transfer.importPage.candidate")}</span>
    </span>
  );
}

function LineView({
  row,
  choice,
  onChange,
  onSearch,
}: {
  row: ImportRow;
  choice: Choice;
  onChange: (choice: Choice) => void;
  onSearch: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const read = [row.values.name, row.values.set, row.values.number].filter(Boolean).join(" · ");
  const chosen = choice.candidate;
  const others = row.candidates.filter(
    (candidate) => candidate.card.print_id !== chosen?.card.print_id,
  );

  return (
    <li className="panel panel-pad flex flex-col gap-3">
      <div className="row-between items-start gap-2">
        <span className="flex min-w-0 flex-col">
          <span className="t-xs t-muted">{t("transfer.importPage.line", { line: row.line })}</span>
          <b className="t-sm truncate">{read || "–"}</b>
          <span className="t-xs t-muted num">
            {row.quantity}× · {row.language.toUpperCase()} · {row.condition}
            {row.purchase_price ? ` · ${money(row.purchase_price, locale) ?? ""}` : ""}
          </span>
        </span>
        <span className={`badge ${STATUS_BADGE[row.status]} shrink-0`}>
          {t(`transfer.status.${row.status}`)}
        </span>
      </div>
      {row.reason || row.problems.length > 0 ? (
        <span className="t-xs t-muted">
          {row.reason ? t(`transfer.reason.${row.reason}`, row.reason) : ""}
          {row.problems.length > 0
            ? `${row.reason ? " · " : ""}${t("transfer.importPage.problems", {
                fields: row.problems
                  .filter((problem) => problem !== "no_card")
                  .map((problem) => t(`transfer.field.${problem}`, problem))
                  .join(", "),
              })}`
            : ""}
        </span>
      ) : null}

      {chosen ? (
        <div className="flex flex-col gap-2">
          <label className="row gap-3">
            <input
              type="checkbox"
              className="size-5 shrink-0"
              checked={choice.include}
              onChange={(event) => {
                onChange({ ...choice, include: event.target.checked });
              }}
              aria-label={t("transfer.importPage.include", { line: row.line })}
            />
            <CandidateCard candidate={chosen} />
          </label>
          <div className="row flex-wrap gap-2 pl-8">
            {chosen.variants.length > 1 ? (
              <select
                className="input w-auto py-1"
                aria-label={t("collection.variant")}
                value={choice.variantId ?? ""}
                onChange={(event) => {
                  onChange({ ...choice, variantId: Number(event.target.value) });
                }}
              >
                {chosen.variants.map((variant) => (
                  <option key={variant.id} value={variant.id}>
                    {variantLabel(variant.variant_key, (key, fallback) => t(key, fallback))}
                  </option>
                ))}
              </select>
            ) : null}
            {row.owned > 0 ? (
              <span className="badge badge-neutral">
                {t("transfer.importPage.owned", { count: row.owned })}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {others.length > 0 && (row.status !== "matched" || !chosen) ? (
        <div className="flex flex-col gap-1">
          <span className="t-xs t-muted">{t("transfer.importPage.choose")}</span>
          <ul className="flex flex-col">
            {others.map((candidate) => (
              <li key={candidate.card.print_id}>
                <button
                  type="button"
                  className="row min-h-12 w-full border-b border-(--border) last:border-0"
                  onClick={() => {
                    onChange(choose(candidate));
                  }}
                >
                  <CandidateCard candidate={candidate} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {row.status !== "matched" ? (
        <button type="button" className="btn btn-ghost btn-sm self-start" onClick={onSearch}>
          <Search />
          {t("transfer.importPage.search")}
        </button>
      ) : null}
    </li>
  );
}

export function ImportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [language, setLanguage] = useState<string>("de");
  const [condition, setCondition] = useState<Condition>("NM");
  const [source, setSource] = useState("Cardmarket");
  const [location, setLocation] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping | null>(null);
  const [choices, setChoices] = useState<Choices>({});
  const [tab, setTab] = useState<Tab>("all");
  const [shown, setShown] = useState(PAGE);
  const [picking, setPicking] = useState<ImportRow | null>(null);

  const preview = useMutation({
    mutationFn: (args: { file: File; mapping: Mapping | null }) =>
      readList(args.file, args.mapping, language, condition),
    onSuccess: (data) => {
      const initial = initialChoices(data.rows);
      setChoices(initial);
      setMapping(data.mapping);
      setTab(summary(data.rows, initial).open > 0 ? "open" : "all");
      setShown(PAGE);
    },
  });
  const pick = useMutation({
    mutationFn: async ({ line, printId }: { line: number; printId: string }) => ({
      line,
      candidate: candidateOf(await fetchCard(printId)),
    }),
    onSuccess: ({ line, candidate }) => {
      setChoices((current) => ({ ...current, [line]: choose(candidate) }));
      setPicking(null);
    },
  });
  const rows = preview.data?.rows ?? [];
  const totals = summary(rows, choices);
  const commit = useMutation({
    mutationFn: async () => {
      const { data, response } = await api.POST("/api/v1/collection/imports", {
        body: { lines: commitLines(rows, choices, source), location_id: location },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: (data) => {
      for (const key of ["collection", "dashboard", "copies", "set-progress"]) {
        void client.invalidateQueries({ queryKey: [key] });
      }
      toast.show({ text: t("transfer.importPage.done", { count: data.created }), icon: <Check /> });
      void navigate({ to: "/collection" });
    },
  });

  const visible = rows.filter((row) => {
    const choice = choiceOf(choices, row);
    if (tab === "open") return isOpen(row, choice);
    if (tab === "ready") return isReady(choice);
    return true;
  });
  const update = (line: number, choice: Choice) => {
    setChoices((current) => ({ ...current, [line]: choice }));
  };
  const read = (nextMapping: Mapping | null) => {
    if (file) preview.mutate({ file, mapping: nextMapping });
  };

  return (
    <div className={`flex flex-col gap-4 ${rows.length > 0 ? "pb-28" : ""}`}>
      <PageHeader back title={t("transfer.importPage.title")} />
      <section className="panel panel-pad flex flex-col gap-4">
        <p className="t-sm t-muted">{t("transfer.importPage.intro")}</p>
        <label className="btn btn-secondary self-start">
          <FileUp />
          {file ? file.name : t("transfer.importPage.file")}
          <input
            type="file"
            accept=".csv,.txt,text/csv"
            className="sr-only"
            onChange={(event) => {
              const chosen = event.target.files?.[0] ?? null;
              setFile(chosen);
              if (chosen) preview.mutate({ file: chosen, mapping: null });
            }}
          />
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="field">
            <span className="field-label">{t("transfer.importPage.defaultLanguage")}</span>
            <select
              className="input"
              value={language}
              onChange={(event) => {
                setLanguage(event.target.value);
              }}
            >
              {LANGUAGES.map((option) => (
                <option key={option} value={option}>
                  {t(`language.${option}`, option.toUpperCase())}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t("transfer.importPage.defaultCondition")}</span>
            <select
              className="input"
              value={condition}
              onChange={(event) => {
                setCondition(event.target.value as Condition);
              }}
            >
              {CONDITIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <SourceInput
            label={t("transfer.importPage.source")}
            value={source}
            onChange={setSource}
          />
        </div>
        <LocationSelect value={location} onChange={setLocation} />
        <span className="t-xs t-muted">{t("transfer.importPage.defaultsHint")}</span>
        {preview.isPending ? (
          <p className="t-sm t-muted">{t("transfer.importPage.reading")}</p>
        ) : null}
        {preview.isError ? (
          <p role="alert" className="t-sm loss">
            {t("transfer.importPage.error")}
          </p>
        ) : null}
      </section>

      {preview.data ? (
        <details className="panel panel-pad" open={totals.lines === 0 && rows.length > 0}>
          <summary className="cursor-pointer font-semibold">
            {t("transfer.importPage.mapping")}
          </summary>
          <p className="t-xs t-muted pt-2">{t("transfer.importPage.mappingHint")}</p>
          <div className="grid grid-cols-1 gap-3 pt-3 sm:grid-cols-2 lg:grid-cols-3">
            {preview.data.fields.map((field) => (
              <label key={field} className="field">
                <span className="field-label">{t(`transfer.field.${field}`, field)}</span>
                <select
                  className="input"
                  value={mapping?.[field] ?? ""}
                  onChange={(event) => {
                    setMapping((current) => ({
                      ...(current ?? {}),
                      [field]: event.target.value || null,
                    }));
                  }}
                >
                  <option value="">{t("transfer.importPage.noColumn")}</option>
                  {preview.data.columns.map((column) => (
                    <option key={column} value={column}>
                      {column}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-secondary mt-3"
            disabled={preview.isPending}
            onClick={() => {
              read(mapping);
            }}
          >
            {t("transfer.importPage.reread")}
          </button>
        </details>
      ) : null}

      {preview.data?.truncated ? (
        <p className="callout callout-warn">{t("transfer.importPage.truncated")}</p>
      ) : null}

      {rows.length > 0 ? (
        <>
          <Segmented
            label={t("transfer.importPage.filter")}
            value={tab}
            onChange={(next) => {
              setTab(next);
              setShown(PAGE);
            }}
            options={[
              { value: "open", label: t("transfer.importPage.tabOpen", { count: totals.open }) },
              { value: "ready", label: t("transfer.importPage.tabReady", { count: totals.lines }) },
              { value: "all", label: t("transfer.importPage.tabAll", { count: rows.length }) },
            ]}
          />
          <ul className="flex flex-col gap-3">
            {visible.slice(0, shown).map((row) => (
              <LineView
                key={row.line}
                row={row}
                choice={choiceOf(choices, row)}
                onChange={(choice) => {
                  update(row.line, choice);
                }}
                onSearch={() => {
                  setPicking(row);
                }}
              />
            ))}
          </ul>
          {visible.length > shown ? (
            <button
              type="button"
              className="btn btn-secondary self-center"
              onClick={() => {
                setShown((current) => current + PAGE);
              }}
            >
              {t("transfer.importPage.more", { count: visible.length - shown })}
            </button>
          ) : null}
          <div className="fixed inset-x-0 bottom-[var(--tabbar-h)] z-20 border-t border-(--border) bg-(--surface) px-4 py-3 md:bottom-0 md:left-(--sidebar-w)">
            <div className="row-between mx-auto max-w-5xl gap-3">
              <span className="t-sm">
                {t("transfer.importPage.lines", { count: totals.lines })} ·{" "}
                {t("transfer.importPage.copies", { count: totals.copies })}
              </span>
              <button
                type="button"
                className="btn btn-primary"
                disabled={totals.lines === 0 || commit.isPending}
                onClick={() => {
                  commit.mutate();
                }}
              >
                {t("transfer.importPage.commit", { count: totals.copies })}
              </button>
            </div>
            {commit.isError ? (
              <p role="alert" className="t-sm loss pt-1 text-center">
                {t("common.saveError")}
              </p>
            ) : null}
          </div>
        </>
      ) : null}

      <Sheet
        open={picking !== null}
        onClose={() => {
          setPicking(null);
        }}
        label={t("transfer.importPage.search")}
      >
        {picking ? (
          <div className="flex flex-col gap-3">
            <h2 className="t-h3">{t("transfer.importPage.search")}</h2>
            <CardPicker
              initialQuery={[picking.values.name, picking.values.number].filter(Boolean).join(" ")}
              onPick={(printId) => {
                pick.mutate({ line: picking.line, printId });
              }}
            />
            {pick.isError ? (
              <p role="alert" className="t-sm loss">
                {t("common.loadError")}
              </p>
            ) : null}
          </div>
        ) : null}
      </Sheet>
    </div>
  );
}
